import React, { useState, useEffect } from 'react';
import { useBasketStore } from '../state/basketStore';
import BarcodeDisplay from './BarcodeDisplay';

/**
 * Check if running in kiosk mode
 */
const isKioskMode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('kiosk') === '1';
};

/**
 * Product card component
 * Shows product image, name, price and optionally add to basket button
 * When hideButton=true, card is clickable for selection (used in catalog view)
 * When showBarcode=true, displays scannable barcode below price (for shelf mode)
 * 
 * BARCODE & LOCATION: Only displayed in kiosk mode (kiosk=1 URL parameter)
 * 
 * PROMOTION LABEL:
 * - Backend should include 'promotion' field in product object
 * - Example: { promotion: "3 for £10" } or { promotion: "Buy 2 Get 1 Free" }
 * - If product.promotion exists, amber label will display between price and barcode
 * - To test: Add promotion: "3 for £10" to any product object in the data
 * 
 * STOCK AVAILABILITY:
 * - catalogSettings.show_stock_availability: Enable/disable stock display
 * - catalogSettings.low_stock_threshold: Threshold for "Only Left" warning
 * - Stock < 0: Shows "Out of Stock", disables add button
 * - Stock < threshold: Shows "Only Left: X" with color coding
 * 
 * ADD TO BASKET:
 * - When hideButton=false, shows quantity selector and Add button
 * - Clicking the button/controls uses stopPropagation() to prevent opening detail panel
 * - Clicking anywhere else on the card opens the product detail panel
 */
export default function ProductCard({ product, hideButton = false, showBarcode = false, catalogSettings = {} }) {
  const kioskMode = isKioskMode();
  
  // Log kiosk mode detection ONCE per component mount
  useEffect(() => {
    console.log('[ProductCard] Kiosk Mode Detection:', {
      url: window.location.href,
      searchParams: window.location.search,
      kioskMode,
      showBarcode
    });
  }, [kioskMode, showBarcode]);
  
  const [imageError, setImageError] = useState(false);
  const [adding, setAdding] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const addItem = useBasketStore(state => state.addItem);
  
  // Extract stock settings
  const showStockAvailability = catalogSettings.show_stock_availability !== false;
  const lowStockThreshold = catalogSettings.low_stock_threshold || 10;
  const qtyOnHand = product.qty_on_hand;
  const isOutOfStock = qtyOnHand !== null && qtyOnHand !== undefined && qtyOnHand < 0;

  const handleAddToBasket = (e) => {
    if (hideButton) return; // Don't add if button is hidden (parent handles click)
    
    e.stopPropagation(); // Prevent parent click handlers
    setAdding(true);
    
    // Compute the full image URL (same logic as display)
    const imageUrl = product.image_url || product.imageUrl;
    const baseUrl = import.meta.env.VITE_RCC_BASE || '';
    const fullImageUrl = imageUrl && !imageUrl.startsWith('http') ? `${baseUrl}${imageUrl}` : imageUrl;
    
    // Pass product with normalized image_url
    addItem({
      ...product,
      image_url: fullImageUrl
    }, quantity);
    
    // Visual feedback
    setTimeout(() => {
      setAdding(false);
      setQuantity(1); // Reset to 1 after adding
    }, 600);
  };

  const incrementQuantity = (e) => {
    e.stopPropagation();
    setQuantity(prev => Math.min(prev + 1, 99));
  };

  const decrementQuantity = (e) => {
    e.stopPropagation();
    setQuantity(prev => Math.max(prev - 1, 1));
  };

  const imageUrl = product.image_url || product.imageUrl;
  const baseUrl = import.meta.env.VITE_RCC_BASE || '';
  const fullImageUrl = imageUrl && !imageUrl.startsWith('http') ? `${baseUrl}${imageUrl}` : imageUrl;
  const showPlaceholder = !fullImageUrl || imageError;
  
  // Debug logging
  if (!imageUrl) {
    console.log("[ProductCard] No image URL for product:", product.product_name || product.name, "Product data:", product);
  }
  
  // Debug barcode rendering
  console.log("[ProductCard] Barcode check:", {
    product: product.product_name || product.name,
    barcode: product.barcode,
    kioskMode,
    showBarcode,
    willShowBarcode: (kioskMode || showBarcode) && product.barcode
  });

  return (
    <div
      className="fade-in"
      style={{
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'opacity 0.2s ease',
        cursor: hideButton ? 'pointer' : 'default',
      }}
    >
      {/* Product Image */}
      <div
        style={{
          aspectRatio: '3/4',
          background: showPlaceholder ? '#f5f5f5' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          marginBottom: '6px',
        }}
      >
        {showPlaceholder ? (
          <div
            style={{
              fontSize: 48,
              color: '#cbd5e1',
            }}
          >
            📦
          </div>
        ) : (
          <img
            src={fullImageUrl}
            alt={product.product_name || product.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
            onError={() => setImageError(true)}
          />
        )}
      </div>

      {/* Product Info */}
      <div
        style={{
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          gap: '0px',
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--color-text)',
            lineHeight: 1.2,
            minHeight: 30,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textAlign: 'center',
            marginBottom: '6px',
          }}
        >
          {product.product_name || product.name}
        </div>

        {/* Variant Badge - Shows when product represents multiple variants.
            Falls back to variantData (attached even in "expanded"/ungrouped
            view, where the backend doesn't set variant_count on each
            individual variant product) so the label shows in both modes. */}
        {(() => {
          const optionsCount = product.variant_count > 1
            ? product.variant_count
            : (product.variantData?.variants?.length > 0 ? product.variantData.variants.length + 1 : 0);
          return optionsCount > 1 && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#6366f1',
                padding: '3px 10px',
                textAlign: 'center',
                marginBottom: '6px',
                display: 'inline-block',
                margin: '0 auto 6px auto',
              }}
            >
              {optionsCount} options
            </div>
          );
        })()}

        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--color-text)',
            textAlign: 'center',
            marginBottom: '2px',
          }}
        >
          £{(product.price || 0).toFixed(2)}
        </div>

        {/* Location Watermark - Shows below price (KIOSK MODE ONLY) */}
        {kioskMode && product.bin_location && (
          <div
            style={{
              fontSize: 8,
              color: 'var(--color-text-secondary)',
              fontWeight: 500,
              textAlign: 'center',
              marginTop: '2px',
              marginBottom: '4px',
              fontFamily: 'monospace',
              opacity: 0.7,
              letterSpacing: '0.3px',
            }}
          >
            loc: {product.bin_location}
          </div>
        )}

        {/* Promotion Label with Stock - Side by side. Promotion visibility depends
            ONLY on product.promotion (matches ProductDetailPanel's shouldShowPromotion) -
            it must never be gated by the unrelated show_stock_availability setting. */}
        {(product.promotion || (showStockAvailability && qtyOnHand !== null)) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              marginTop: '6px',
              marginBottom: '4px',
            }}
          >
            {/* Promotion Label */}
            {product.promotion && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #fb923c, #f43f5e)',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: '6px',
                  textAlign: 'center',
                  letterSpacing: '0.3px',
                  boxShadow: '0 2px 6px rgba(244, 63, 94, 0.35)',
                  flex: showStockAvailability && qtyOnHand !== null && qtyOnHand !== undefined ? '0 0 auto' : '1',
                }}
              >
                {typeof product.promotion === 'string' ? product.promotion : product.promotion.text}
              </div>
            )}
            
            {/* Stock Availability */}
            {showStockAvailability && qtyOnHand !== null && qtyOnHand !== undefined && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isOutOfStock ? '#dc2626' : qtyOnHand <= lowStockThreshold ? '#d97706' : '#059669',
                  textAlign: 'right',
                  fontFamily: 'system-ui',
                  whiteSpace: 'nowrap',
                  marginLeft: 'auto',
                  padding: '2px 6px',
                }}
              >
                {isOutOfStock ? 'Out of Stock' : qtyOnHand <= lowStockThreshold ? `Only ${qtyOnHand} left` : `${qtyOnHand} in stock`}
              </div>
            )}
          </div>
        )}

        {/* Barcode - Shows below promotion with matching height */}
        {/* Shows in: Kiosk mode OR Shelf mode (showBarcode=true) */}
        {(kioskMode || showBarcode) && product.barcode && (
          <div style={{ 
            marginTop: '6px', 
            marginBottom: '4px',
            maxHeight: '30px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            <BarcodeDisplay 
              key={product.barcode || product.sku_id} 
              barcode={product.barcode} 
              width={1.2} 
              height={16} 
              fontSize={7} 
            />
          </div>
        )}

        {!hideButton && (
          <div
            style={{
              display: 'flex',
              gap: '6px',
              marginTop: '10px',
              alignItems: 'stretch',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Quantity Selector */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#f8fafc',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                minWidth: kioskMode ? '110px' : '90px',
              }}
            >
              <button
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: quantity <= 1 ? '#cbd5e1' : '#64748b',
                  fontSize: kioskMode ? '20px' : '18px',
                  fontWeight: 700,
                  cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                  padding: kioskMode ? '8px 12px' : '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (quantity > 1) e.currentTarget.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                −
              </button>
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: kioskMode ? '16px' : '14px',
                  fontWeight: 600,
                  color: '#1e293b',
                  padding: '0 8px',
                }}
              >
                {quantity}
              </div>
              <button
                onClick={incrementQuantity}
                disabled={quantity >= 99}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: quantity >= 99 ? '#cbd5e1' : '#64748b',
                  fontSize: kioskMode ? '20px' : '18px',
                  fontWeight: 700,
                  cursor: quantity >= 99 ? 'not-allowed' : 'pointer',
                  padding: kioskMode ? '8px 12px' : '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (quantity < 99) e.currentTarget.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                +
              </button>
            </div>

            {/* Add to Basket Button */}
            <button
              onClick={handleAddToBasket}
              disabled={adding || isOutOfStock}
              style={{
                flex: 1,
                background: adding ? '#d1fae5' : isOutOfStock ? '#f1f5f9' : '#f0fdfa',
                color: adding ? '#065f46' : isOutOfStock ? '#94a3b8' : '#0f766e',
                border: adding ? '1px solid #a7f3d0' : isOutOfStock ? '1px solid #cbd5e1' : '1px solid #99f6e4',
                borderRadius: '8px',
                fontSize: kioskMode ? '14px' : '12px',
                fontWeight: 600,
                cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                padding: kioskMode ? '9px 14px' : '7px 10px',
              }}
              onMouseEnter={(e) => {
                if (!adding && !isOutOfStock) {
                  e.currentTarget.style.background = '#ccfbf1';
                  e.currentTarget.style.borderColor = '#5eead4';
                }
              }}
              onMouseLeave={(e) => {
                if (!adding && !isOutOfStock) {
                  e.currentTarget.style.background = '#f0fdfa';
                  e.currentTarget.style.borderColor = '#99f6e4';
                }
              }}
            >
              {adding ? (
                <>
                  <span>✓</span>
                  <span>Added!</span>
                </>
              ) : isOutOfStock ? (
                <>
                  <span>✗</span>
                  <span>Out of Stock</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: kioskMode ? '18px' : '16px' }}>🛒</span>
                  <span>Add</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
